import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@copro/api';
import { useSpace } from '../../../hooks/useSpace';
import type { QuoteStatus } from '../../../types';
import { invalidatePendingQuoteViews } from './invalidatePendingQuoteViews';
import { keys } from './keys';

type UpdateQuoteStatusCommand = {
  quoteId: string;
  subjectId: string;
  status: QuoteStatus;
  dateApproved?: string | null;
  approvedDocumentUrl?: string | null;
  clearApprovedDocumentUrl?: boolean;
};

async function updateSubjectQuoteStatus(command: UpdateQuoteStatusCommand) {
  const updateData: {
    status: string;
    date_approved: string | null;
    approved_document_url?: string | null;
  } = {
    status: command.status,
    date_approved:
      command.status === 'accepted'
        ? command.dateApproved ?? new Date().toISOString()
        : null,
  };

  if (command.status === 'accepted') {
    if (command.approvedDocumentUrl !== undefined) {
      updateData.approved_document_url = command.approvedDocumentUrl;
    }
  } else if (command.clearApprovedDocumentUrl === true) {
    updateData.approved_document_url = null;
  } else if (command.clearApprovedDocumentUrl === false) {
    // Keep existing approved_document_url
  } else if (command.status === 'pending') {
    updateData.approved_document_url = null;
  }

  const { data, error } = await apiClient
    .from('subject_quotes')
    .update(updateData)
    .eq('id', command.quoteId)
    .select('id, status, date_approved, approved_document_url')
    .single();

  if (error) {
    throw error;
  }

  return data;
}

export function useUpdateSubjectQuoteStatus() {
  const queryClient = useQueryClient();
  const { currentSpace } = useSpace();

  return useMutation({
    mutationFn: (command: UpdateQuoteStatusCommand) => updateSubjectQuoteStatus(command),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.subTopicQuotes(variables.subjectId) });
      queryClient.invalidateQueries({ queryKey: keys.subTopic(variables.subjectId) });
      queryClient.invalidateQueries({ queryKey: [...keys.all, 'activityTimeline'] });
      if (currentSpace?.id) {
        invalidatePendingQuoteViews(queryClient, currentSpace.id);
      }
    },
  });
}
